import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  Requirement,
  RequirementCategory,
  RequiredForm,
  FormatRule,
  ProposalAddendum,
} from "../types";
import { uploadRfpDocument, deleteStorageDocument } from "./storageService";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

/**
 * Subscribes to the requirements subcollection of an opportunity.
 */
export function subscribeToRequirements(
  opportunityId: string,
  callback: (requirements: Requirement[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "requirements");
  const q = query(colRef, orderBy("createdAt", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: Requirement[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          sectionRef: data.sectionRef || undefined,
          title: String(data.title || ""),
          prompt: String(data.prompt || ""),
          category: data.category || "general",
          mandatory: Boolean(data.mandatory),
          sourcePage: data.sourcePage ?? undefined,
          status: data.status || "unanswered",
          assignedTo: data.assignedTo || undefined,
          assignedToEmail: data.assignedToEmail || undefined,
          source: data.source || "manual",
          humanEdited: Boolean(data.humanEdited),
          confirmed: Boolean(data.confirmed),
          extractionJobId: data.extractionJobId || undefined,
          matchedAnswerId: data.matchedAnswerId || undefined,
          addendumTags: Array.isArray(data.addendumTags) ? data.addendumTags : [],
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });
      callback(items);
    },
    (error) => {
      console.error("Error subscribing to requirements:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a new manual requirement for an opportunity.
 */
export async function createRequirement(
  opportunityId: string,
  data: Omit<Requirement, "id" | "opportunityId" | "createdAt" | "updatedAt">,
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, "opportunities", opportunityId, "requirements");
  const cleanData = sanitizeForFirestore({
    ...data,
    opportunityId,
    source: data.source || "manual",
    humanEdited: true,
    confirmed: Boolean(data.confirmed),
    createdAt: now,
    updatedAt: now,
  });
  const docRef = await addDoc(colRef, cleanData);
  return docRef.id;
}

/**
 * Updates a requirement, marking it as human edited.
 */
export async function updateRequirement(
  opportunityId: string,
  requirementId: string,
  updates: Partial<Requirement>,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "requirements", requirementId);
  const cleanUpdates = sanitizeForFirestore({
    ...updates,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
  await updateDoc(docRef, cleanUpdates);
}

/**
 * Toggles a requirement's confirmed status.
 */
export async function toggleRequirementConfirmed(
  opportunityId: string,
  requirementId: string,
  currentConfirmed: boolean,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "requirements", requirementId);
  await updateDoc(docRef, {
    confirmed: !currentConfirmed,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Assigns a requirement to a staff member.
 */
export async function assignRequirement(
  opportunityId: string,
  requirementId: string,
  staff: { displayName: string; email: string } | null,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "requirements", requirementId);
  await updateDoc(docRef, {
    assignedTo: staff ? staff.displayName : null,
    assignedToEmail: staff ? staff.email : null,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Deletes a requirement from an opportunity.
 */
export async function deleteRequirement(
  opportunityId: string,
  requirementId: string,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "requirements", requirementId);
  await deleteDoc(docRef);
}

// ============================================================================
// REQUIRED FORMS
// ============================================================================

/**
 * Subscribes to the required_forms subcollection of an opportunity.
 */
export function subscribeToRequiredForms(
  opportunityId: string,
  callback: (forms: RequiredForm[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "required_forms");
  const q = query(colRef, orderBy("createdAt", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: RequiredForm[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          formName: String(data.formName || ""),
          description: String(data.description || ""),
          mandatory: Boolean(data.mandatory),
          sourcePage: data.sourcePage ?? undefined,
          status: data.status || "not_started",
          assignedTo: data.assignedTo || undefined,
          assignedToEmail: data.assignedToEmail || undefined,
          fileName: data.fileName || undefined,
          notes: data.notes || undefined,
          vaultDocId: data.vaultDocId || undefined,
          storagePath: data.storagePath || undefined,
          addendumTags: Array.isArray(data.addendumTags) ? data.addendumTags : [],
          source: data.source || "manual",
          humanEdited: Boolean(data.humanEdited),
          confirmed: Boolean(data.confirmed),
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });
      callback(items);
    },
    (error) => {
      console.error("Error subscribing to required forms:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a manual required form.
 */
export async function createRequiredForm(
  opportunityId: string,
  data: Omit<RequiredForm, "id" | "opportunityId" | "createdAt" | "updatedAt">,
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, "opportunities", opportunityId, "required_forms");
  const cleanData = sanitizeForFirestore({
    ...data,
    opportunityId,
    source: data.source || "manual",
    humanEdited: true,
    confirmed: Boolean(data.confirmed),
    createdAt: now,
    updatedAt: now,
  });
  const docRef = await addDoc(colRef, cleanData);
  return docRef.id;
}

/**
 * Updates a required form.
 */
export async function updateRequiredForm(
  opportunityId: string,
  formId: string,
  updates: Partial<RequiredForm>,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "required_forms", formId);
  const cleanUpdates = sanitizeForFirestore({
    ...updates,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
  await updateDoc(docRef, cleanUpdates);
}

/**
 * Deletes a required form.
 */
export async function deleteRequiredForm(opportunityId: string, formId: string): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "required_forms", formId);
  await deleteDoc(docRef);
}

// ============================================================================
// FORMAT RULES
// ============================================================================

/**
 * Subscribes to the format_rules subcollection of an opportunity.
 */
export function subscribeToFormatRules(
  opportunityId: string,
  callback: (rules: FormatRule[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "format_rules");
  const q = query(colRef, orderBy("createdAt", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: FormatRule[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          ruleType: data.ruleType || "other",
          requirement: String(data.requirement || ""),
          sourcePage: data.sourcePage ?? undefined,
          isCompliant: data.isCompliant !== undefined ? Boolean(data.isCompliant) : true,
          notes: data.notes || undefined,
          source: data.source || "manual",
          humanEdited: Boolean(data.humanEdited),
          confirmed: Boolean(data.confirmed),
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });
      callback(items);
    },
    (error) => {
      console.error("Error subscribing to format rules:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a manual format rule.
 */
export async function createFormatRule(
  opportunityId: string,
  data: Omit<FormatRule, "id" | "opportunityId" | "createdAt" | "updatedAt">,
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, "opportunities", opportunityId, "format_rules");
  const cleanData = sanitizeForFirestore({
    ...data,
    opportunityId,
    source: data.source || "manual",
    humanEdited: true,
    confirmed: Boolean(data.confirmed),
    createdAt: now,
    updatedAt: now,
  });
  const docRef = await addDoc(colRef, cleanData);
  return docRef.id;
}

/**
 * Updates a format rule.
 */
export async function updateFormatRule(
  opportunityId: string,
  ruleId: string,
  updates: Partial<FormatRule>,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "format_rules", ruleId);
  const cleanUpdates = sanitizeForFirestore({
    ...updates,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
  await updateDoc(docRef, cleanUpdates);
}

/**
 * Deletes a format rule.
 */
export async function deleteFormatRule(opportunityId: string, ruleId: string): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "format_rules", ruleId);
  await deleteDoc(docRef);
}

// ============================================================================
// BULK OPERATIONS
// ============================================================================

export interface BulkUpdateRequirementsPatch {
  assignedTo?: string | null;
  assignedToEmail?: string | null;
  category?: RequirementCategory;
  mandatory?: boolean;
  confirmed?: boolean;
}

/**
 * Updates multiple requirements in batches of <= 450 documents.
 * Marks all modified documents with humanEdited: true and updatedAt.
 */
export async function bulkUpdateRequirements(
  opportunityId: string,
  requirementIds: string[],
  patch: BulkUpdateRequirementsPatch,
): Promise<void> {
  if (requirementIds.length === 0) return;
  const now = new Date().toISOString();
  const chunkSize = 450;
  for (let i = 0; i < requirementIds.length; i += chunkSize) {
    const chunk = requirementIds.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    for (const reqId of chunk) {
      const docRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
      batch.update(docRef, {
        ...patch,
        humanEdited: true,
        updatedAt: now,
      });
    }
    await batch.commit();
  }
}

/**
 * Deletes multiple requirements and strips their IDs from any associated addenda.
 * Executes in batches of <= 450 operations.
 */
export async function bulkDeleteRequirements(
  opportunityId: string,
  requirementIds: string[],
  addenda: ProposalAddendum[] = [],
): Promise<void> {
  if (requirementIds.length === 0) return;
  const now = new Date().toISOString();
  const reqIdSet = new Set(requirementIds);

  // 1. Clean up references in addenda
  for (const addendum of addenda) {
    if (addendum.affectedRequirementIds?.some((id) => reqIdSet.has(id))) {
      const cleaned = addendum.affectedRequirementIds.filter((id) => !reqIdSet.has(id));
      const addendumRef = doc(db, "opportunities", opportunityId, "addenda", addendum.id);
      await updateDoc(addendumRef, {
        affectedRequirementIds: cleaned,
        updatedAt: now,
      });
    }
  }

  // 2. Delete requirements in batches
  const chunkSize = 450;
  for (let i = 0; i < requirementIds.length; i += chunkSize) {
    const chunk = requirementIds.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    for (const reqId of chunk) {
      const docRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
      batch.delete(docRef);
    }
    await batch.commit();
  }
}

// ============================================================================
// REQUIRED FORMS ENHANCEMENTS
// ============================================================================

/**
 * Assigns a required form to a team member.
 */
export async function assignRequiredForm(
  opportunityId: string,
  formId: string,
  staff: { displayName: string; email: string } | null,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "required_forms", formId);
  await updateDoc(docRef, {
    assignedTo: staff ? staff.displayName : null,
    assignedToEmail: staff ? staff.email : null,
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Uploads an executed or completed form file and updates form status to 'attached'.
 */
export async function attachFormFile(
  opportunityId: string,
  formId: string,
  file: File,
): Promise<{ storagePath: string; fileName: string }> {
  const uploadResult = await uploadRfpDocument(opportunityId, file);
  const docRef = doc(db, "opportunities", opportunityId, "required_forms", formId);
  await updateDoc(docRef, {
    storagePath: uploadResult.storagePath,
    fileName: file.name,
    status: "attached",
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
  return { storagePath: uploadResult.storagePath, fileName: file.name };
}

/**
 * Detaches an uploaded form file and deletes the Storage document.
 */
export async function detachFormFile(
  opportunityId: string,
  formId: string,
  currentStoragePath?: string,
): Promise<void> {
  if (currentStoragePath) {
    try {
      await deleteStorageDocument(currentStoragePath);
    } catch (err) {
      console.warn(`Failed to delete storage document at ${currentStoragePath}:`, err);
    }
  }
  const docRef = doc(db, "opportunities", opportunityId, "required_forms", formId);
  await updateDoc(docRef, {
    storagePath: null,
    fileName: null,
    status: "not_started",
    humanEdited: true,
    updatedAt: new Date().toISOString(),
  });
}
